import { APP_DEV_LOCALHOST, getApplicationDomain, getApplicationEndpoint } from '@gitbook/env';
import { parseUrl } from '@gitbook/util-url';

import { resolveLocationToNavigationScreen } from './location-to-screen';
import { resolveNavigationScreenToLocation } from './screen-to-location';
import type { LocationDescriptor, NavigationScreenState } from './types';

/**
 * Return an absolute url for a navigation screen
 */
export function resolveNavigationScreenToURL(screen: NavigationScreenState): string {
    const location = resolveNavigationScreenToLocation(screen);
    return resolveLocationDescriptorToURL(getApplicationEndpoint(), location);
}

/**
 * Return true if the given url is an application URL.
 */
export function isApplicationURL(url: string): boolean {
    const parsed = parseUrl(url);
    if (!parsed) {
        return false;
    }

    return parsed.host === getApplicationDomain();
}

/**
 * Resolve an absolute url to a navigation screen
 */
export function resolveURLToNavigation(input: string): NavigationScreenState | null {
    const url = parseUrl(input);

    if (!url) {
        return null;
    }

    // We use url.host instead of url.hostname because url.host includes the port
    // which is important for local development.
    if (url.host !== getApplicationDomain() && url.host !== APP_DEV_LOCALHOST) {
        return null;
    }

    return resolveLocationToNavigationScreen({
        pathname: url.pathname,
        search: url.search,
        hash: url.hash ? url.hash.slice(1) : undefined,
    });
}

/**
 * Resolves a location descriptor to a URL, using the given hostname as a base.
 *
 * Accepts partial locations.
 */
export function resolveLocationDescriptorToURL(
    hostname: string,
    location: Partial<LocationDescriptor>
): string {
    const url = new URL(hostname);

    if (location.pathname !== undefined) {
        url.pathname = location.pathname;
    }

    url.search = location.search || '';

    if (location.hash) {
        url.hash = `#${location.hash}`;
    }

    return url.toString();
}
